import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  EventEmitter,
  forwardRef,
  Input,
  input,
  model,
  Output,
  signal,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';

export interface OptionsObj {
  label: string;
  value: string | number | null;
}

type Height = 'small' | 'medium';

@Component({
  standalone: true,
  selector: 'app-select-dropdown',
  templateUrl: './select-dropdown.component.html',
  styleUrls: ['./select-dropdown.component.css'],
  imports: [CommonModule, TranslateModule, FormsModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectDropdownComponent),
      multi: true,
    },
  ],
})
export class SelectDropdownComponent implements ControlValueAccessor {
  @Input() options: string[] = [];
  @Input() optionsObj: OptionsObj[] = [];
  @Output() handleSelect = new EventEmitter<string | number | null>();
  @Input() height: Height = 'medium';
  selected = model<string | null>(null);
  disabledInput = input(false, { alias: 'disabled' });
  placeholder = input('Select an Option');
  showPlaceholder = input(true);

  private readonly formDisabled = signal(false);
  isDisabled = computed(() => this.disabledInput() || this.formDisabled());

  private onChange: (value: string | number | null) => void = () => {
    // placeholder for ControlValueAccessor
  };
  private onTouched: () => void = () => {
    // placeholder for ControlValueAccessor
  };

  writeValue(value: string | number | null): void {
    this.selected.set(
      value === null || value === undefined ? '' : String(value)
    );
  }

  registerOnChange(fn: (value: string | number | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.formDisabled.set(isDisabled);
  }

  optionValue(value: string | number | null): string {
    return value === null || value === undefined ? '' : String(value);
  }

  onSelect(rawValue: string | number | null): void {
    const raw =
      rawValue === null || rawValue === undefined ? '' : String(rawValue);
    const parsedValue = this.resolveValue(raw);

    this.selected.set(raw);
    this.onChange(parsedValue);
    this.onTouched();
    this.handleSelect.emit(parsedValue);
  }

  onBlur(): void {
    this.onTouched();
  }

  private resolveValue(raw: string): string | number | null {
    if (this.optionsObj.length > 0) {
      const match = this.optionsObj.find(
        (option) => this.optionValue(option.value) === raw
      );
      return match ? match.value : raw;
    }

    return raw;
  }
}
